import { computed } from 'vue';

import { competitiveRouteNames } from '@/app/competitive/constants';
import { BRAND } from '@/models/auth/permissions.enum';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';

export function usePrimaryNavigationMenu() {
  const identityStore = useIdentityStore();
  const flagStore = useFlagStore();
  const currentBrandLabel = computed(() => identityStore.currentBrand?.label);
  const brandCanAccessCreators = computed(() =>
    identityStore.guard(BRAND.CREATORS.CAN_ACCESS_CREATORS),
  );
  const brandCanAccessRelationships = computed(() =>
    identityStore.guard(BRAND.INSTAGRAM.CAN_ACCESS_RELATIONSHIPS),
  );
  const brandCanAccessRelationshipsOnly = computed(
    () => !brandCanAccessCreators.value && brandCanAccessRelationships.value,
  );

  const primaryNavigationItems = computed(() => [
    {
      backgroundClass: 'nav-bg-dashboards',
      hoverBackgroundClass: 'hover:nav-bg-dashboards',
      icon: 'dashboards',
      label: 'Dashboards',
      link: 'dashboards',
      name: 'dashboards',
      visible: true,
      routeHasBrandLabel: false,
    },
    {
      backgroundClass: 'nav-bg-analytics',
      hoverBackgroundClass: 'hover:nav-bg-analytics',
      icon: 'analytics',
      label: 'Analytics',
      link: 'instagram',
      name: 'analytics',
      visible: true,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-creators',
      hoverBackgroundClass: 'hover:nav-bg-creators',
      icon: 'creators',
      label: 'Relationships',
      link: 'relationships',
      name: 'relationships',
      visible: brandCanAccessRelationshipsOnly.value,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-creators',
      hoverBackgroundClass: 'hover:nav-bg-creators',
      icon: 'creators',
      label: 'Creators',
      link: 'creators',
      name: 'creators',
      visible: !brandCanAccessRelationshipsOnly.value,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-campaigns',
      hoverBackgroundClass: 'hover:nav-bg-campaigns',
      icon: 'campaigns',
      label: 'Campaigns',
      link: 'campaigns',
      name: 'campaigns',
      visible: true,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-community',
      hoverBackgroundClass: 'hover:nav-bg-community',
      icon: 'community',
      label: 'Community',
      link: 'community',
      name: 'community',
      visible: true,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-competitors',
      hoverBackgroundClass: 'hover:nav-bg-competitors',
      icon: 'competitors',
      label: 'Competitors',
      link: competitiveRouteNames.instagram.main,
      name: 'competitors',
      visible: true,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-scheduler',
      hoverBackgroundClass: 'hover:nav-bg-scheduler',
      icon: 'publishing',
      label: 'Scheduler',
      link: 'scheduler',
      name: 'scheduler',
      visible: true,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-library',
      hoverBackgroundClass: 'hover:nav-bg-library',
      icon: 'library',
      label: 'Library',
      link: 'library',
      name: 'library',
      visible: true,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-listening',
      hoverBackgroundClass: 'hover:nav-bg-listening',
      icon: 'listening',
      label: 'Listening',
      link: 'socialListening',
      name: 'listening',
      visible: true,
      routeHasBrandLabel: true,
    },
    {
      backgroundClass: 'nav-bg-creators',
      hoverBackgroundClass: 'hover:nav-bg-creators',
      icon: 'magic-wand',
      label: 'Constellation Pro',
      link: 'constellation-pro',
      name: 'constellation-pro',
      visible: flagStore.ready && flagStore.flags.constellationPro,
      routeHasBrandLabel: true,
    },
  ]);

  const visibleNavigationItems = computed(() =>
    primaryNavigationItems.value
      .filter((platform) => platform.visible)
      .map((platform) => {
        const to: { name: string; params?: { brandLabel: string | undefined } } = {
          name: platform.link,
        };
        if (platform.routeHasBrandLabel) {
          to.params = { brandLabel: currentBrandLabel.value };
        }
        return {
          to,
          ...platform,
        };
      }),
  );

  return {
    visibleNavigationItems,
  };
}
